import axios from '../utils/axios';
import { AxiosResponse } from 'axios';

export interface ModulePermission {
    module: string;
    // Optional sub-module (e.g. inventory -> "grn"). Omitted/null = whole module.
    subModule?: string | null;
    permissions: string[];
    scope: string;
}

export interface AvailableSubModule {
    id: string;
    name: string;
    route?: string;
    permissions: string[];
}

// source: 'user' = saved on the user, 'derived' = added by manager/location settings, 'full-access' = admin overlay.
export interface ModuleAccessEntry {
    permissions: string[];
    scope: string;
    subModules?: Record<string, { permissions: string[]; scope: string }>;
    source?: 'role' | 'user' | 'derived' | 'full-access';
}

export interface ContextPermissions {
    isManager?: boolean;
    canApproveLeaves?: boolean;
    canApproveAttendance?: boolean;
    canApproveTimesheets?: boolean;
    canApproveExpenses?: boolean;
    approvalScope?: string;
    canAssignRoles?: boolean;
    maxRoleLevel?: number;
}

export interface UserPermissions {
    baseRole: string | null;
    basePermissions: any[];
    additionalRoles: any[];
    moduleAccess: Record<string, ModuleAccessEntry>;
    contextPermissions: ContextPermissions;
    effectivePermissions: string[];
    // Allowed location ids. Empty = all locations (no restriction).
    locations?: string[];
    canApprove: {
        leaves: boolean;
        attendance: boolean;
        timesheets: boolean;
        expenses: boolean;
        scope: string;
    };
    isManager: boolean;
    isAccountOwner?: boolean;
    // Admin-level access overlay: this user has everything the account owner has,
    // while their assigned role (baseRole) stays exactly as it is.
    hasFullAccess?: boolean;
}

export interface PermissionHistoryEntry {
    _id: string;
    action: string;
    changeType: string;
    changes: {
        before: any;
        after: any;
    };
    module?: string;
    permissions?: string[];
    reason?: string;
    performedBy: {
        name: string;
        email: string;
    };
    performedAt: string;
}

export interface AvailableModule {
    id: string;
    name: string;
    description: string;
    permissions: string[];
    route?: string;
    subModules?: AvailableSubModule[];
}

export interface AvailableScope {
    id: string;
    name: string;
    description: string;
}

class PermissionService {
    private baseURL = '/api/v1/permissions';

    async getUserPermissions(userId: string): Promise<UserPermissions> {
        const response: AxiosResponse<{ data: UserPermissions }> = await axios.get(
            `${this.baseURL}/user/${userId}`
        );
        return response.data.data;
    }

    async checkPermission(
        userId: string, 
        resource: string, 
        action: string, 
        context?: any
    ): Promise<boolean> {
        const params = { resource, action };
        if (context) {
            (params as any).context = JSON.stringify(context);
        }

        const response: AxiosResponse<{ data: { hasPermission: boolean } }> = await axios.get(
            `${this.baseURL}/user/${userId}/check`,
            { params }
        );
        return response.data.data.hasPermission;
    }

    async grantModuleAccess(
        userId: string,
        moduleData: ModulePermission,
        reason?: string
    ): Promise<any> {
        const response = await axios.post(
            `${this.baseURL}/user/${userId}/grant`,
            { ...moduleData, reason }
        );
        return response.data;
    }

    async revokeModuleAccess(
        userId: string,
        module: string,
        reason?: string,
        subModule?: string | null,
        permissions?: string[]
    ): Promise<any> {
        const response = await axios.delete(
            `${this.baseURL}/user/${userId}/module/${module}`,
            { data: { reason, subModule: subModule || null, permissions } }
        );
        return response.data;
    }

    async updateContextPermissions(
        userId: string,
        contextPermissions: ContextPermissions,
        reason?: string
    ): Promise<any> {
        const response = await axios.put(
            `${this.baseURL}/user/${userId}/context`,
            { contextPermissions, reason }
        );
        return response.data;
    }

    async getPermissionHistory(userId: string, limit = 50): Promise<PermissionHistoryEntry[]> {
        const response: AxiosResponse<{ data: PermissionHistoryEntry[] }> = await axios.get(
            `${this.baseURL}/user/${userId}/history`,
            { params: { limit } }
        );
        return response.data.data;
    }

    async getUsersWithModuleAccess(module?: string, permission?: string): Promise<any[]> {
        const params: any = {};
        if (module) params.module = module;
        if (permission) params.permission = permission;

        const response: AxiosResponse<{ data: any[] }> = await axios.get(
            `${this.baseURL}/users`,
            { params }
        );
        return response.data.data;
    }

    async getAvailableModules(): Promise<{
        modules: AvailableModule[];
        scopes: AvailableScope[];
    }> {
        const response: AxiosResponse<{
            data: {
                modules: AvailableModule[];
                scopes: AvailableScope[];
            }
        }> = await axios.get(`${this.baseURL}/modules`);
        return response.data.data;
    }

    async setUserLocations(userId: string, locations: string[], reason?: string, defaultLocation?: string | null): Promise<any> {
        const response = await axios.put(
            `${this.baseURL}/user/${userId}/locations`,
            { locations, reason, defaultLocation }
        );
        return response.data;
    }

    // Grant/revoke admin-level access for a user. Their role is NOT changed.
    async setFullAccess(userId: string, enabled: boolean, reason?: string): Promise<boolean> {
        const response = await axios.put(
            `${this.baseURL}/user/${userId}/full-access`,
            { enabled, reason }
        );
        return !!response.data?.data?.fullAccess;
    }

    // May the LOGGED-IN user grant admin-level access? (owner / Admin only)
    async canGrantFullAccess(): Promise<boolean> {
        const response = await axios.get(`${this.baseURL}/can-grant-full-access`);
        return !!response.data?.data?.allowed;
    }

    async getEnforcement(): Promise<boolean> {
        const response = await axios.get(`${this.baseURL}/enforcement`);
        return !!response.data?.data?.enabled;
    }

    async setEnforcement(enabled: boolean): Promise<boolean> {
        const response = await axios.put(`${this.baseURL}/enforcement`, { enabled });
        return !!response.data?.data?.enabled;
    }

    async getManagerialUsers(): Promise<any[]> {
        const response: AxiosResponse<{ data: any[] }> = await axios.get(
            `${this.baseURL}/managers`
        );
        return response.data.data;
    }

    // Convenience methods
    async hasModulePermission(
        userId: string,
        module: string,
        permission: string,
        scope?: string
    ): Promise<boolean> {
        return this.checkPermission(userId, module, permission, { scope });
    }

    async canApprove(
        userId: string,
        type: 'leaves' | 'attendance' | 'timesheets' | 'expenses',
        targetUserId?: string
    ): Promise<boolean> {
        const context = targetUserId ? { targetUserId } : undefined;
        return this.checkPermission(userId, type, 'approve', context);
    }
}

export const permissionService = new PermissionService();
export default permissionService;